import React, { useState } from "react";
import {
  Table, Tag, Button, Input, Modal, Avatar, Badge, Tooltip,
  Card, Tabs, Grid, Pagination, message,
} from "antd";
import {
  CheckOutlined, CloseOutlined, CalendarOutlined,
  UserOutlined, ClockCircleOutlined, FileTextOutlined,
  CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetDoctorsVacations,
  useRespondToVacation,
  useRespondToSpecialistVacation,
} from "../../../hooks/Usevacations";
import { getImg } from "../../../Api/getImg";
import VacationsFilterBar from "./FilterBar";

const BRAND = "#1F6F7D";
const BRAND_LIGHT = "#e8f4f7";

// ── Status helpers ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  0: { label: "Pending", color: "warning", icon: <ClockCircleOutlined /> },
  1: { label: "Approved", color: "success", icon: <CheckCircleOutlined /> },
  2: { label: "Rejected", color: "error", icon: <CloseCircleOutlined /> },
};

const sectionLabel = {
  fontSize: 11,
  color: "#9ca3af",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

// ── Mobile card ───────────────────────────────────────────────────────────────
const MobileVacationCard = ({ record, onOpen }) => {
  const s = STATUS_MAP[record.status] || STATUS_MAP[0];
  const pending = record.status === 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <Avatar
          src={getImg(record.doctorPhoto)}
          icon={<UserOutlined />}
          size={40}
          style={{ background: BRAND, flexShrink: 0 }}
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-800 text-sm truncate">{record.doctorName}</div>
          <div className="text-xs text-gray-400 truncate">{record.specialization}</div>
          {record.isConsultant && (
            <Tag color="cyan" style={{ fontSize: 10, padding: "0 5px", marginTop: 2 }}>
              Consultant
            </Tag>
          )}
        </div>
        <Tag color={s.color} icon={s.icon} style={{ margin: 0, fontWeight: 600, borderRadius: 8 }}>
          {s.label}
        </Tag>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-700 font-medium">
        <CalendarOutlined style={{ color: BRAND }} />
        <span>{dayjs(record.startDate).format("MMM D, YYYY")}</span>
        <span className="text-gray-400">→</span>
        <span>{dayjs(record.endDate).format("MMM D, YYYY")}</span>
        <span className="text-gray-400">
          ({record.durationDays} day{record.durationDays !== 1 ? "s" : ""})
        </span>
      </div>

      {record.reason && (
        <div
          className="mt-2 text-xs text-gray-600"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {record.reason}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500 flex items-center gap-2">
          Affected bookings
          <Badge
            count={record.affectedBookingsCount}
            showZero
            style={{
              background: record.affectedBookingsCount > 0 ? "#f59e0b" : "#d1d5db",
              color: record.affectedBookingsCount > 0 ? "#fff" : "#6b7280",
              fontWeight: 700,
            }}
          />
        </span>
        <Button
          size="small"
          icon={pending ? <FileTextOutlined /> : <InfoCircleOutlined />}
          onClick={() => onOpen(record)}
          style={
            pending
              ? { background: BRAND, color: "#fff", border: "none", borderRadius: 7, fontWeight: 600 }
              : { color: BRAND, borderColor: BRAND, borderRadius: 7 }
          }
        >
          {pending ? "Respond" : "Details"}
        </Button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const VacationsScreen = () => {
  const screens = Grid.useBreakpoint();
  // `screens` is {} on the first render, so only treat as mobile once we know md is false
  const isMobile = screens.md === false;

  const [activeTab, setActiveTab] = useState("all"); // "all" | "consultant" | "specialists"
  const [respondModal, setRespondModal] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 10,
    searchDoctorName: "",
    status: null,
    fromDate: null,
    toDate: null,
    isConsultant: null,
  });

  // Sync isConsultant with active tab
  const effectiveFilters = {
    ...filters,
    isConsultant: activeTab === "all" ? null : activeTab === "specialists" ? false : true,
  };

  const { data, isLoading } = useGetDoctorsVacations(effectiveFilters);
  const respondMutation = useRespondToVacation();
  const respondSpecialistMutation = useRespondToSpecialistVacation();

  const vacations = data?.vacations || [];
  const totalCount = data?.totalCount || 0;

  const openRecord = (record) => {
    setRespondModal(record);
    setAdminNote(record.status === 0 ? "" : record.adminNote || "");
  };

  const closeModal = () => {
    setRespondModal(null);
    setAdminNote("");
  };

  // ── Respond handler ────────────────────────────────────────────────────────
  const handleRespond = async (isApproved) => {
    if (!respondModal) return;
    const payload = {
      vacationId: respondModal.vacationId,
      isApproved,
      adminNote,
    };
    try {
      if (respondModal.isConsultant) {
        await respondSpecialistMutation.mutateAsync(payload);
      } else {
        await respondMutation.mutateAsync(payload);
      }
      message.success(`Vacation ${isApproved ? "approved" : "rejected"} successfully`);
      closeModal();
    } catch (err) {
      message.error(err?.response?.data?.errors?.[0] || "Failed to respond");
    }
  };

  // ── Columns (desktop / tablet) ─────────────────────────────────────────────
  const columns = [
    {
      title: <span className="text-white font-semibold">Doctor</span>,
      dataIndex: "doctorName",
      render: (name, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={getImg(record.doctorPhoto)}
            icon={<UserOutlined />}
            size={40}
            style={{ background: BRAND, flexShrink: 0 }}
          />
          <div>
            <div className="font-semibold text-gray-800 text-sm">{name}</div>
            <div className="text-xs text-gray-400">{record.specialization}</div>
            {record.isConsultant && (
              <Tag color="cyan" style={{ fontSize: 10, padding: "0 5px", marginTop: 2 }}>
                Consultant
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Period</span>,
      width: 270,
      render: (_, record) => (
        <div>
          <div className="flex text-xs items-center gap-1 text-gray-700 font-medium">
            <CalendarOutlined style={{ color: BRAND }} />
            <span>{dayjs(record.startDate).format("MMM D, YYYY")}</span>
            <span className="text-gray-400 mx-1">→</span>
            <span>{dayjs(record.endDate).format("MMM D, YYYY")}</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            <ClockCircleOutlined className="mr-1" />
            {record.durationDays} day{record.durationDays !== 1 ? "s" : ""}
          </div>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Reason</span>,
      dataIndex: "reason",
      ellipsis: true,
      render: (reason) => (
        <Tooltip title={reason}>
          <span className="text-gray-600 text-sm">{reason || "—"}</span>
        </Tooltip>
      ),
    },
    {
      title: <span className="text-white font-semibold">Affected Bookings</span>,
      dataIndex: "affectedBookingsCount",
      align: "center",
      width: 140,
      render: (count) => (
        <Badge
          count={count}
          showZero
          style={{
            background: count > 0 ? "#f59e0b" : "#d1d5db",
            color: count > 0 ? "#fff" : "#6b7280",
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      title: <span className="text-white font-semibold">Status</span>,
      dataIndex: "status",
      align: "center",
      width: 120,
      render: (status) => {
        const s = STATUS_MAP[status] || STATUS_MAP[0];
        return (
          <Tag
            color={s.color}
            icon={s.icon}
            style={{ fontWeight: 600, fontSize: 12, padding: "3px 10px", borderRadius: 8 }}
          >
            {s.label}
          </Tag>
        );
      },
    },
    {
      title: <span className="text-white font-semibold">Actions</span>,
      align: "center",
      width: 120,
      fixed: "right",
      render: (_, record) =>
        record.status === 0 ? (
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => openRecord(record)}
            style={{
              background: BRAND, color: "#fff", border: "none",
              borderRadius: 7, fontWeight: 600, fontSize: 12,
            }}
          >
            Respond
          </Button>
        ) : (
          <Tooltip title={record.adminNote || "No admin note"}>
            <Button
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() => openRecord(record)}
              style={{ borderRadius: 7, fontSize: 12, color: BRAND, borderColor: BRAND }}
            >
              Details
            </Button>
          </Tooltip>
        ),
    },
  ];

  const tableProps = {
    columns,
    dataSource: vacations,
    loading: isLoading,
    rowKey: "vacationId",
    scroll: { x: 900 },
    pagination: {
      current: filters.pageNumber,
      pageSize: filters.pageSize,
      total: totalCount,
      showTotal: (total) => `Total ${total} vacations`,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "50"],
      className: "px-4 py-4",
      onChange: (page, pageSize) =>
        setFilters({ ...filters, pageNumber: page, pageSize }),
    },
  };

  // ── Shared tab content ─────────────────────────────────────────────────────
  const content = (
    <>
      <VacationsFilterBar filters={filters} onChange={setFilters} />
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {isLoading && <div className="text-center text-gray-400 py-6">Loading...</div>}
          {!isLoading && vacations.length === 0 && (
            <div className="text-center text-gray-400 py-6">No vacations found</div>
          )}
          {vacations.map((v) => (
            <MobileVacationCard key={v.vacationId} record={v} onOpen={openRecord} />
          ))}
          {totalCount > filters.pageSize && (
            <div className="flex justify-center py-2">
              <Pagination
                simple
                current={filters.pageNumber}
                pageSize={filters.pageSize}
                total={totalCount}
                showSizeChanger={false}
                onChange={(page) => setFilters({ ...filters, pageNumber: page })}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden shadow-lg">
          <Table {...tableProps} />
        </div>
      )}
    </>
  );

  const tabLabel = (icon, text) => (
    <span className="px-1 sm:px-4 py-2 font-semibold text-sm sm:text-base">
      {React.cloneElement(icon, { className: "mr-1 sm:mr-2" })}
      {text}
    </span>
  );

  const tabItems = [
    { key: "all", label: tabLabel(<UserOutlined />, isMobile ? "All" : "All Doctors"), children: content },
    { key: "consultant", label: tabLabel(<CheckCircleOutlined />, "Consultants"), children: content },
    { key: "specialists", label: tabLabel(<CheckCircleOutlined />, "Specialists"), children: content },
  ];

  const isResponding = respondMutation.isPending || respondSpecialistMutation.isPending;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-2 sm:p-6">
      <Card className="border-0 rounded-2xl overflow-hidden [&_.ant-card-body]:!p-3 sm:[&_.ant-card-body]:!p-6">
        <style>{`
          .ant-table-thead > tr > th {
            background: linear-gradient(135deg, #1F6F7D 0%, #1F6F7D 100%) !important;
            color: white !important;
            font-weight: 600 !important;
            border: none !important;
            padding: 16px !important;
            font-size: 14px !important;
          }
          .ant-table-thead > tr > th::before { display: none !important; }
          .ant-table-tbody > tr > td {
            padding: 14px 16px !important;
            border-bottom: 1px solid #f0f0f0 !important;
          }
          @media (max-width: 640px) {
            .ant-table-thead > tr > th { padding: 10px !important; font-size: 12px !important; }
            .ant-table-tbody > tr > td { padding: 10px !important; }
          }
        `}</style>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setFilters((f) => ({ ...f, pageNumber: 1 }));
          }}
          size={isMobile ? "middle" : "large"}
          items={tabItems}
          tabBarStyle={{
            marginBottom: isMobile ? 12 : 24,
            borderBottom: "2px solid #e8e8e8",
            paddingLeft: isMobile ? 0 : 8,
          }}
        />
      </Card>

      {/* ── Respond / Details Modal ── */}
      <Modal
        open={!!respondModal}
        onCancel={closeModal}
        footer={null}
        width={520}
        style={{ maxWidth: "calc(100vw - 24px)", top: isMobile ? 12 : 100 }}
        destroyOnClose
        title={
          <div className="flex items-center gap-3">
            <CalendarOutlined style={{ color: BRAND, fontSize: 18 }} />
            <span className="font-bold text-base sm:text-lg">
              {respondModal?.status === 0 ? "Respond to Vacation" : "Vacation Details"}
            </span>
          </div>
        }
      >
        {respondModal && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
            {/* Doctor info */}
            <div
              style={{
                background: BRAND_LIGHT, borderRadius: 10, padding: "14px 16px",
                display: "flex", alignItems: "center", gap: 14,
              }}
            >
              <Avatar
                src={getImg(respondModal.doctorPhoto)}
                icon={<UserOutlined />}
                size={48}
                style={{ background: BRAND, flexShrink: 0 }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1f2937" }}>
                  {respondModal.doctorName}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{respondModal.specialization}</div>
                {respondModal.isConsultant && (
                  <Tag color="cyan" style={{ marginTop: 4 }}>Consultant</Tag>
                )}
              </div>
            </div>

            {/* Period */}
            <div
              style={{
                background: "#f9fafb", borderRadius: 8, padding: "12px 14px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ ...sectionLabel, marginBottom: 8 }}>Vacation Period</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 8, margin: 0 }}>
                  {dayjs(respondModal.startDate).format("MMM D, YYYY")}
                </Tag>
                <span style={{ color: "#9ca3af" }}>→</span>
                <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 8, margin: 0 }}>
                  {dayjs(respondModal.endDate).format("MMM D, YYYY")}
                </Tag>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  ({respondModal.durationDays} days)
                </span>
              </div>
            </div>

            {/* Reason */}
            {respondModal.reason && (
              <div>
                <div style={{ ...sectionLabel, marginBottom: 6 }}>Reason</div>
                <div
                  style={{
                    background: "#f9fafb", borderRadius: 8, padding: "12px 14px",
                    fontSize: 14, color: "#374151", lineHeight: 1.7,
                    border: "1px solid #e5e7eb", wordBreak: "break-word",
                  }}
                >
                  {respondModal.reason}
                </div>
              </div>
            )}

            {/* Affected bookings */}
            {respondModal.affectedBookingsCount > 0 && (
              <div
                style={{
                  background: "#fffbeb", borderRadius: 8, padding: "10px 14px",
                  border: "1px solid #fcd34d", display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <InfoCircleOutlined style={{ color: "#f59e0b" }} />
                <span style={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
                  This vacation affects {respondModal.affectedBookingsCount} booking(s)
                </span>
              </div>
            )}

            {/* Current status (if already responded) */}
            {respondModal.status !== 0 && (
              <div>
                <div style={{ ...sectionLabel, marginBottom: 6 }}>Decision</div>
                <Tag
                  color={STATUS_MAP[respondModal.status]?.color}
                  icon={STATUS_MAP[respondModal.status]?.icon}
                  style={{ fontSize: 13, padding: "4px 14px", borderRadius: 8, fontWeight: 600 }}
                >
                  {STATUS_MAP[respondModal.status]?.label}
                </Tag>
              </div>
            )}

            {/* Admin note */}
            <div>
              <div style={{ ...sectionLabel, marginBottom: 6 }}>
                Admin Note {respondModal.status === 0 ? "(optional)" : ""}
              </div>
              <Input.TextArea
                rows={3}
                placeholder="Add a note for the doctor..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                disabled={respondModal.status !== 0}
                style={{ borderRadius: 8, fontSize: 13 }}
              />
            </div>

            {/* Actions — only for pending */}
            {respondModal.status === 0 && (
              <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:justify-end pt-1">
                <Button
                  icon={<CloseOutlined />}
                  danger
                  loading={isResponding}
                  onClick={() => handleRespond(false)}
                  className="w-full sm:w-auto"
                  style={{ borderRadius: 8, fontWeight: 600, minWidth: 110 }}
                >
                  Reject
                </Button>
                <Button
                  icon={<CheckOutlined />}
                  loading={isResponding}
                  onClick={() => handleRespond(true)}
                  className="w-full sm:w-auto"
                  style={{
                    background: BRAND, color: "#fff", border: "none",
                    borderRadius: 8, fontWeight: 600, minWidth: 110,
                  }}
                >
                  Approve
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VacationsScreen;